import { forwardRef, useId } from 'react';

/**
 * Single-line text input with a mandatory label, optional hint and error
 * (spec §7.1, §12). Error text is wired via `aria-describedby` + `aria-invalid`;
 * hint text likewise. Only one of hint/error renders at a time — the error
 * takes precedence, matching `.etch-field[aria-invalid="true"]` in fields.css.
 *
 * @param {object} props
 * @param {string} [props.id] - generated via useId when omitted.
 * @param {import('react').ReactNode} props.label
 * @param {boolean} [props.hideLabel] - visually hides the label via
 *   `.etch-visually-hidden` while keeping it in the accessibility tree.
 * @param {import('react').ReactNode} [props.hint]
 * @param {import('react').ReactNode} [props.error]
 * @param {boolean} [props.required]
 * @param {string} [props.className] - applied to the outer `.etch-field`.
 * @param {string} [props.inputClassName] - applied to the `.etch-input`.
 */
export const TextField = forwardRef(function TextField(
  { id, label, hideLabel = false, hint, error, required, className = '', inputClassName = '', ...rest },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={`etch-field ${className}`.trim()}>
      <label htmlFor={inputId} className={`etch-field__label${hideLabel ? ' etch-visually-hidden' : ''}`}>
        {label}
        {required && (
          <span className="etch-field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <input
        ref={ref}
        id={inputId}
        className={`etch-input ${inputClassName}`.trim()}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        {...rest}
      />
      {error ? (
        <span id={errorId} className="etch-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={hintId} className="etch-field__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
});

export default TextField;
